/**
 * Session-bootstrap gate.
 *
 * Wraps the route tree so protected routes never render before the
 * `refresh + me` round trip resolves. While bootstrap is pending, a
 * full-page loader is shown. Once bootstrap reaches `ready` or `guest`
 * we hand off to the router; route guards (`GuestRoute`,
 * `ProtectedRoute`, `BusinessGuard`) take it from there.
 *
 * The loader is intentionally minimal — full-page bootstrap loaders
 * should not reuse the in-app shell, otherwise users see protected
 * chrome before the session is confirmed.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionBootstrap } from '@/features/auth/hooks/ui/use-session-bootstrap';

interface SessionBootstrapProps {
  children: ReactNode;
}

export function SessionBootstrap({ children }: SessionBootstrapProps) {
  const status = useSessionBootstrap();
  const { t } = useTranslation();

  if (status === 'pending') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-neutral-50"
      >
        <div className="flex flex-col items-center gap-3 text-neutral-600">
          <div
            aria-hidden
            className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600"
          />
          <span className="text-sm">{t('common:loading', { defaultValue: 'Loading…' })}</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
